import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { NoopT } from 'declarations/types/common';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useDeleteAttachment } from 'hooks/api/useAttachments';

type DeleteAttachmentParamsT = {
  ticketId: string;
  fileName: string;
  originalName: string;
};

type UseDeleteAttachmentPopupT = {
  isDeletingAttachment: boolean;
  isAttachmentfullyDeleted: boolean;
  isAttachmentDeletionError: boolean;
  deleteAttachmentErrorMessage: string | undefined;
  deleteAttachmentValidationErrorMessage: string | undefined;
  showPopupContent: boolean;
  handleDeleteAttachment: (params: DeleteAttachmentParamsT) => NoopT;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
};

const useDeleteAttachmentPopup = (): UseDeleteAttachmentPopupT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();

  /**
   * Api hooks
   */
  const deleteAttachmentMutation = useDeleteAttachment({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketAttachments]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
    },
  });

  /**
   * Variables
   */
  const deleteAttachmentErrorMessage = useMemo(
    () => getErrorMessage(deleteAttachmentMutation.error),
    [deleteAttachmentMutation.error]
  );
  const deleteAttachmentValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(deleteAttachmentMutation.error),
    [deleteAttachmentMutation.error]
  );

  /**
   * Handlers
   */
  const resetDeleteAttachmentMutationState = useCallback(() => {
    if (deleteAttachmentMutation.isSuccess || deleteAttachmentMutation.isError) {
      deleteAttachmentMutation.reset();
    }
  }, [deleteAttachmentMutation]);
  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
      resetDeleteAttachmentMutationState();
    },
    [resetDeleteAttachmentMutationState]
  );
  const handleDeleteAttachment = useCallback(
    (params: DeleteAttachmentParamsT) => () => {
      deleteAttachmentMutation.mutate(params);
    },
    [deleteAttachmentMutation]
  );

  return {
    isDeletingAttachment: deleteAttachmentMutation.isLoading,
    isAttachmentfullyDeleted: deleteAttachmentMutation.isSuccess,
    isAttachmentDeletionError: deleteAttachmentMutation.isError,
    deleteAttachmentErrorMessage,
    deleteAttachmentValidationErrorMessage,
    showPopupContent: deleteAttachmentMutation.isIdle || deleteAttachmentMutation.isLoading,
    handleDeleteAttachment,
    handleClosePopup,
  };
};

export default useDeleteAttachmentPopup;
